import { Controller } from "@hotwired/stimulus"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * The reading layer, working on the story the hero just wrote.
 *
 * Order matters here, because it is the order it happens in the product: a
 * reader marks a passage, the note docks in the margin, a second reader marks a
 * sentence as a question, the thread opens under it, someone upvotes.
 *
 * Replays on re-entry so a visitor who scrolls back sees it again.
 */
export default class extends Controller {
  static targets = ["mark", "card", "thread", "count", "arrow"]

  static values = { step: { type: Number, default: 520 } }

  connect() {
    if (window.matchMedia(REDUCED).matches) return

    this.timers = []
    this.baseCounts = this.countTargets.map((el) => parseInt(el.textContent, 10) || 0)

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.play()
        } else {
          this.reset()
        }
      },
      { threshold: 0, rootMargin: "-20% 0px -20% 0px" }
    )

    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    this.clearTimers()
  }

  play() {
    if (this.playing) return
    this.playing = true

    const step = this.stepValue
    let t = 260

    // Marks and their matching margin cards are paired by document order.
    this.markTargets.forEach((mark, i) => {
      this.after(t, () => mark.classList.add("is-marked"))
      const card = this.cardTargets[i]
      if (card) this.after(t + step * 0.85, () => card.classList.add("is-in"))
      t += step * 1.6
    })

    this.threadTargets.forEach((thread) => {
      this.after(t, () => thread.classList.add("is-open"))
      t += step * 0.7
    })

    this.countTargets.forEach((el, i) => {
      this.after(t, () => {
        el.textContent = String(this.baseCounts[i] + 1)
        el.classList.add("is-bumped")
        const arrow = this.arrowTargets[i]
        if (arrow) arrow.classList.add("is-cast")
      })
      t += step * 0.5
    })
  }

  reset() {
    this.clearTimers()
    this.playing = false
    this.markTargets.forEach((el) => el.classList.remove("is-marked"))
    this.cardTargets.forEach((el) => el.classList.remove("is-in"))
    this.threadTargets.forEach((el) => el.classList.remove("is-open"))
    this.arrowTargets.forEach((el) => el.classList.remove("is-cast"))
    this.countTargets.forEach((el, i) => {
      el.classList.remove("is-bumped")
      el.textContent = String(this.baseCounts[i])
    })
  }

  after(ms, fn) {
    this.timers.push(setTimeout(fn, ms))
  }

  clearTimers() {
    ;(this.timers || []).forEach(clearTimeout)
    this.timers = []
  }
}
